import React, { useEffect, useState, createContext, useContext } from 'react';
import { flushSync } from 'react-dom';
interface CartItem {
  id: string;
  name: string;
  price: number;
  quantity: number;
  image?: string;
}
interface CartContextType {
  items: CartItem[];
  addToCart: () => void;
  removeItem: (id: string) => void;
  updateQuantity: (id: string, quantity: number) => void;
  clearCart: () => void;
  totalItems: number;
  totalPrice: number;
  isOpen: boolean;
  openCart: () => void;
  closeCart: () => void;
}
const CartContext = createContext<CartContextType | undefined>(undefined);
const PRODUCT = {
  id: 'crowncontrol-device',
  name: 'CrownControl™ Device',
  price: 497,
  image: 'https://cdn.magicpatterns.com/uploads/ddWEZuz6pnzGe64ipqMMCE/ChatGPT_Image_Feb_19,_2026,_03_27_07_PM.png'
};
export function CartProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [items, setItems] = useState<CartItem[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  // Debug logging
  useEffect(() => {
    console.log('Cart state changed:', {
      itemCount: items.length,
      isOpen
    });
  }, [items, isOpen]);
  const addToCart = () => {
    console.log('addToCart called - current state:', {
      itemCount: items.length,
      isOpen
    });
    // Use flushSync to force synchronous state updates
    flushSync(() => {
      setItems(prev => {
        const existing = prev.find(item => item.id === PRODUCT.id);
        if (existing) {
          console.log('Incrementing existing item');
          return prev.map(item => item.id === PRODUCT.id ? {
            ...item,
            quantity: item.quantity + 1
          } : item);
        }
        console.log('Adding new item');
        return [...prev, {
          ...PRODUCT,
          quantity: 1
        }];
      });
    });
    // Now open the cart - this happens after items are updated
    flushSync(() => {
      console.log('Setting isOpen to true');
      setIsOpen(true);
    });
    console.log('addToCart completed');
  };
  const removeItem = (id: string) => {
    console.log('removeItem called:', id);
    setItems(prev => prev.filter(item => item.id !== id));
  };
  const updateQuantity = (id: string, quantity: number) => {
    console.log('updateQuantity called:', {
      id,
      quantity
    });
    if (quantity <= 0) {
      removeItem(id);
      return;
    }
    setItems(prev => prev.map(item => item.id === id ? {
      ...item,
      quantity
    } : item));
  };
  const clearCart = () => {
    console.log('clearCart called');
    setItems([]);
  };
  const openCart = () => {
    console.log('openCart called');
    setIsOpen(true);
  };
  const closeCart = () => {
    console.log('closeCart called');
    setIsOpen(false);
  };
  const totalItems = items.reduce((sum, item) => sum + item.quantity, 0);
  const totalPrice = items.reduce((sum, item) => sum + item.price * item.quantity, 0);
  return <CartContext.Provider value={{
    items,
    addToCart,
    removeItem,
    updateQuantity,
    clearCart,
    totalItems,
    totalPrice,
    isOpen,
    openCart,
    closeCart
  }} data-id="element-341">
      {children}
    </CartContext.Provider>;
}
export function useCart() {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
}